/**
 * @file ChallengeSettingsTab.jsx
 * @description Settings tab component for MaterialChallenge
 */

import React, { useEffect, useState } from "react";
import { ConfirmationModal } from "../../ConfirmationModal";
import { Icon } from "../../Icon";

export const ChallengeSettingsTab = ({ 
  challengeID, 
  challengeData, 
  userChallengeData, 
  user, 
  challengeMembers,
  isLoadingMembers,
  isChallengeAdmin,
  hasAnyAdmin,
  adminUpdateUserId,
  isRenamingChallenge,
  isClaimingAdmin,
  isRemovingMember,
  showRemoveMemberModal,
  removeMemberTarget,
  showAdminRoleModal,
  adminRoleTarget,
  adminRoleNextValue,
  fetchUserChallengeData,
  onRenameChallenge,
  onToggleAdminRole,
  onClaimAdmin,
  onRemoveMember,
  onAdminRoleClick,
  onConfirmAdminRoleChange,
  onCloseAdminRoleModal,
  onConfirmRemoveMember,
  onCloseRemoveMemberModal,
  isArchiving,
  setIsArchiving,
  isLeavingJoining,
  setIsLeavingJoining,
  userIsInChallenge,
  setUserIsInChallenge,
  datesToChapters,
  handleArchiveChallenge,
  handleLeaveJoinChallenge,
  showArchiveModal,
  setShowArchiveModal,
  showLeaveModal,
  setShowLeaveModal,
  confirmArchiveChallenge,
  confirmLeaveChallenge,
  onToggleEmailUpdates,
  isUpdatingEmail,
  challengeType,
  goalPercent,
  onUpdateChallengeType,
  onUpdateGoalPercent,
  isUpdatingChallengeType,
  isUpdatingGoalPercent,
  inviteEmail = '',
  setInviteEmail = () => {},
  isInvitingByEmail = false,
  onInviteByEmail = () => {},
  onOpenInviteFromChallenge = () => {},
  showInviteFromChallengeModal = false,
  inviteStep = 'selectChallenge',
  setInviteStep = () => {},
  inviteChallenges = [],
  isLoadingInviteChallenges = false,
  selectedInviteChallengeId = null,
  onSelectInviteChallenge = () => {},
  inviteChallengeMembers = [],
  selectedInviteMemberIds = new Set(),
  onToggleInviteMember = () => {},
  onSelectAllInviteMembers = () => {},
  onConfirmInviteMembers = () => {},
  onCloseInviteFromChallenge = () => {},
  isLoadingInviteMembers = false,
  isAddingInviteMembers = false
}) => {
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeDataURL, setQRCodeDataURL] = useState('');
  const [renameValue, setRenameValue] = useState(challengeData?.challenge_name || '');
  const [localChallengeType, setLocalChallengeType] = useState(challengeType || 'leaderboard');
  const [goalInput, setGoalInput] = useState(
    Number.isFinite(goalPercent) ? String(goalPercent) : ''
  );
  const selectedInviteChallenge = inviteChallenges.find(
    (challenge) => challenge.id === selectedInviteChallengeId
  );

  const challengeUrl = `${window.location.origin}/challenge?id=${challengeID}`;
  const emailUpdatesEnabled = userChallengeData?.emailUpdates !== false;

  useEffect(() => {
    setRenameValue(challengeData?.challenge_name || '');
  }, [challengeData?.challenge_name]);

  useEffect(() => {
    setLocalChallengeType(challengeType || 'leaderboard');
    setGoalInput(Number.isFinite(goalPercent) ? String(goalPercent) : '');
  }, [challengeType, goalPercent]);


  const handlePWAShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: challengeData?.challenge_name || 'Bible Reading Challenge',
          text: `Join me in reading the Bible! ${challengeData?.challenge_name || 'Challenge'}`,
          url: challengeUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(challengeUrl);
      alert('Challenge link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      const textArea = document.createElement('textarea');
      textArea.value = challengeUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Challenge link copied to clipboard!');
    }
  };

  const handleShowQRCode = async () => {
    try {
      const QRCode = await import('qrcode');
      const dataURL = await QRCode.toDataURL(challengeUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQRCodeDataURL(dataURL);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code');
    }
  };

  return (
    <div style={{ height: '100%', minHeight: 0, padding: '16px', overflow: 'auto' }}>
      <h2 style={{ color: 'var(--md-sys-color-on-surface)', marginBottom: '16px' }}>Challenge Settings</h2>
      
      {/* Sharing Section */}
      <div style={{ 
        backgroundColor: 'var(--md-sys-color-surface-container-low)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: 'var(--md-sys-color-on-surface)' }}>Sharing</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button 
            onClick={handlePWAShare}
            style={{
              backgroundColor: 'var(--md-sys-color-primary)',
              color: 'var(--md-sys-color-on-primary)',
              border: 'none',
              borderRadius: '20px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon name="share" />
            Share Challenge
          </button>
          <button 
            onClick={handleShowQRCode}
            style={{
              backgroundColor: 'transparent',
              color: 'var(--md-sys-color-primary)',
              border: '1px solid var(--md-sys-color-outline)',
              borderRadius: '20px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon name="qrCode" />
            QR Code
          </button>
        </div>
      </div>

      {user && userIsInChallenge && userChallengeData && !userChallengeData?.archived && (
        <div style={{ 
          backgroundColor: 'var(--md-sys-color-surface-container-low)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--md-sys-color-on-surface)' }}>Email Updates</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Icon
              name={emailUpdatesEnabled ? "emailEnabled" : "emailDisabled"}
              style={{ color: emailUpdatesEnabled ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-error)' }}
            />
            <div>
              <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500' }}>
                {emailUpdatesEnabled ? 'Email updates are on.' : 'Email updates are off.'}
              </div>
              <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                Based on your email time in settings.
              </div>
            </div>
          </div>
          <button
            onClick={onToggleEmailUpdates}
            {...(isUpdatingEmail ? { disabled: true } : {})}
            aria-label={emailUpdatesEnabled ? 'Turn off emails' : 'Turn on emails'}
            style={{
              backgroundColor: 'transparent',
              color: emailUpdatesEnabled ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)',
              border: `1px solid ${emailUpdatesEnabled ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)'}`,
              borderRadius: '20px',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isUpdatingEmail ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isUpdatingEmail ? 0.7 : 1
            }}
          >
            {isUpdatingEmail ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid currentColor',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                {emailUpdatesEnabled ? 'Turning off...' : 'Turning on...'}
              </>
            ) : (
              <>
                <Icon name={emailUpdatesEnabled ? "notificationsOff" : "notificationsOn"} />
                {emailUpdatesEnabled ? 'Turn off emails' : 'Turn on emails'}
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Challenge Actions - Only show if user is logged in */}
      {user && (
        <div style={{ 
          backgroundColor: 'var(--md-sys-color-surface-container-low)',
          borderRadius: '12px',
          padding: '16px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--md-sys-color-on-surface)' }}>Challenge Actions</h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {userIsInChallenge && (
              <button 
                onClick={handleArchiveChallenge}
                {...(isArchiving ? { disabled: true } : {})}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--md-sys-color-primary)',
                  border: '1px solid var(--md-sys-color-outline)',
                  borderRadius: '20px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isArchiving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isArchiving ? 0.7 : 1
                }}
              >
                {isArchiving ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid currentColor',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    {userChallengeData?.archived ? 'Unarchiving...' : 'Archiving...'}
                  </>
                ) : (
                  <>
                    <Icon name={userChallengeData?.archived ? "unarchiveChallenge" : "archiveChallenge"} />
                    {userChallengeData?.archived ? 'Unarchive' : 'Archive'}
                  </>
                )}
              </button>
            )}
            <button 
              onClick={handleLeaveJoinChallenge}
              {...(isLeavingJoining ? { disabled: true } : {})}
              style={{
                backgroundColor: 'transparent',
                color: userIsInChallenge ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)',
                border: `1px solid ${userIsInChallenge ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)'}`,
                borderRadius: '20px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLeavingJoining ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLeavingJoining ? 0.7 : 1
              }}
            >
              {isLeavingJoining ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid currentColor',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  {userIsInChallenge ? 'Leaving...' : 'Joining...'}
                </>
              ) : (
                <>
                  <Icon name={userIsInChallenge ? "leaveChallenge" : "joinChallenge"} />
                  {userIsInChallenge ? 'Leave Challenge' : 'Join Challenge'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {user && userIsInChallenge && isChallengeAdmin && (
        <div style={{ 
          backgroundColor: 'var(--md-sys-color-surface-container-low)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '16px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--md-sys-color-on-surface)' }}>Admin</h3>
          <>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', marginBottom: '8px' }}>
                  Challenge Type
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {['leaderboard', 'cooperative'].map((type) => {
                    const isSelected = localChallengeType === type;
                    const label = type === 'leaderboard' ? 'Leaderboard' : 'Cooperative';
                    const isDisabled = isUpdatingChallengeType || isSelected;
                    return (
                      <button
                        key={type}
                        onClick={() => {
                          setLocalChallengeType(type);
                          onUpdateChallengeType(type);
                        }}
                        {...(isDisabled ? { disabled: true } : {})}
                        style={{
                          backgroundColor: isSelected
                            ? 'var(--md-sys-color-primary)'
                            : 'transparent',
                          color: isSelected
                            ? 'var(--md-sys-color-on-primary)'
                            : 'var(--md-sys-color-primary)',
                          border: isSelected
                            ? '1px solid var(--md-sys-color-primary)'
                            : '1px solid var(--md-sys-color-outline)',
                          borderRadius: '20px',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.7 : 1
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {localChallengeType === 'cooperative' && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', marginBottom: '8px' }}>
                    Group Goal (%)
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={goalInput}
                      onChange={(event) => setGoalInput(event.target.value)}
                      placeholder="0-100"
                      style={{
                        flex: '1',
                        minWidth: '120px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid var(--md-sys-color-outline)',
                        backgroundColor: 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => onUpdateGoalPercent(Number(goalInput))}
                      {...((isUpdatingGoalPercent || goalInput === '' || Number.isNaN(Number(goalInput)) || Number(goalInput) < 0 || Number(goalInput) > 100) ? { disabled: true } : {})}
                      style={{
                        backgroundColor: 'var(--md-sys-color-primary)',
                        color: 'var(--md-sys-color-on-primary)',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: (isUpdatingGoalPercent || goalInput === '' || Number.isNaN(Number(goalInput)) || Number(goalInput) < 0 || Number(goalInput) > 100) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: (isUpdatingGoalPercent || goalInput === '' || Number.isNaN(Number(goalInput)) || Number(goalInput) < 0 || Number(goalInput) > 100) ? 0.7 : 1
                      }}
                    >
                      {isUpdatingGoalPercent ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid currentColor',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Icon name="flag" />
                          Save Goal
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', marginBottom: '8px' }}>
                  Rename Challenge
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    placeholder="Challenge name"
                    style={{
                      flex: '1',
                      minWidth: '200px',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1px solid var(--md-sys-color-outline)',
                      backgroundColor: 'var(--md-sys-color-surface)',
                      color: 'var(--md-sys-color-on-surface)',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => onRenameChallenge(renameValue)}
                    {...(isRenamingChallenge || !renameValue.trim() ? { disabled: true } : {})}
                    style={{
                      backgroundColor: 'var(--md-sys-color-primary)',
                      color: 'var(--md-sys-color-on-primary)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '10px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isRenamingChallenge || !renameValue.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: isRenamingChallenge || !renameValue.trim() ? 0.7 : 1
                    }}
                  >
                    {isRenamingChallenge ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid currentColor',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Icon name="edit" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', marginBottom: '8px' }}>
                  Invite
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      placeholder="Email address"
                      style={{
                        flex: '1',
                        minWidth: '200px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid var(--md-sys-color-outline)',
                        backgroundColor: 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={onInviteByEmail}
                      {...((isInvitingByEmail || !inviteEmail.trim()) ? { disabled: true } : {})}
                      style={{
                        backgroundColor: 'var(--md-sys-color-primary)',
                        color: 'var(--md-sys-color-on-primary)',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: (isInvitingByEmail || !inviteEmail.trim()) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: (isInvitingByEmail || !inviteEmail.trim()) ? 0.7 : 1
                      }}
                    >
                      {isInvitingByEmail ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid currentColor',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Adding...
                        </>
                      ) : (
                        <>
                          <Icon name="personAdd" />
                          Add by email
                        </>
                      )}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={onOpenInviteFromChallenge}
                      {...(isLoadingInviteChallenges ? { disabled: true } : {})}
                      style={{
                        backgroundColor: 'transparent',
                        color: 'var(--md-sys-color-primary)',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '20px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: isLoadingInviteChallenges ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: isLoadingInviteChallenges ? 0.7 : 1
                      }}
                    >
                      <Icon name="joinChallenge" />
                      Add users from challenge
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', marginBottom: '8px' }}>
                  Admins
                </div>
                {isLoadingMembers ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                    <md-circular-progress indeterminate></md-circular-progress>
                    Loading members...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {challengeMembers.map((member) => {
                      const isAdmin = member.role === "admin";
                      const isUpdating = adminUpdateUserId === member.id;
                      const label = member.id === user.uid ? `${member.displayName} (you)` : member.displayName;
                      const canRemove = member.id !== user.uid;
                      return (
                        <div
                          key={member.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            padding: '8px 12px',
                            borderRadius: '10px',
                            border: '1px solid var(--md-sys-color-outline-variant)',
                            backgroundColor: 'var(--md-sys-color-surface)'
                          }}
                        >
                          <div>
                            <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', fontSize: '14px' }}>
                              {label}
                            </div>
                            <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                              {member.email || member.id}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={() => onAdminRoleClick(member, !isAdmin)}
                              {...(isUpdating ? { disabled: true } : {})}
                              aria-label={isAdmin ? 'Remove admin' : 'Make admin'}
                              style={{
                                backgroundColor: 'transparent',
                                color: isAdmin ? 'var(--md-sys-color-error)' : 'var(--md-sys-color-primary)',
                                border: isAdmin
                                  ? '1px solid var(--md-sys-color-error)'
                                  : '1px solid var(--md-sys-color-primary)',
                                borderRadius: '20px',
                                padding: '8px',
                                fontSize: '12px',
                                fontWeight: '500',
                                cursor: isUpdating ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: isUpdating ? 0.7 : 1
                              }}
                            >
                              {isUpdating ? (
                                <>
                                  <div style={{
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid currentColor',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }}></div>
                                </>
                              ) : (
                                <Icon
                                  name={isAdmin ? "removeModerator" : "verifiedUser"}
                                  style={{ fontSize: '16px' }}
                                />
                              )}
                            </button>
                            {canRemove && (
                              <button
                                onClick={() => onRemoveMember(member)}
                                {...(isRemovingMember ? { disabled: true } : {})}
                                aria-label="Remove member"
                                style={{
                                  backgroundColor: 'transparent',
                                  color: 'var(--md-sys-color-error)',
                                  border: '1px solid var(--md-sys-color-error)',
                                  borderRadius: '20px',
                                  padding: '8px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  cursor: isRemovingMember ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  opacity: isRemovingMember ? 0.7 : 1
                                }}
                              >
                                <Icon name="removePerson" style={{ fontSize: '16px' }} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {challengeMembers.length === 0 && (
                      <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                        No members found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
        </div>
      )}

      {showInviteFromChallengeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--md-sys-color-surface)',
            borderRadius: '12px',
            padding: '20px',
            width: '92vw',
            maxWidth: '520px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              <h3 style={{ color: 'var(--md-sys-color-on-surface)', margin: 0 }}>
                Add users from challenge
              </h3>
              <button
                onClick={onCloseInviteFromChallenge}
                {...(isAddingInviteMembers ? { disabled: true } : {})}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--md-sys-color-primary)',
                  border: '1px solid var(--md-sys-color-outline)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: isAddingInviteMembers ? 'not-allowed' : 'pointer',
                  opacity: isAddingInviteMembers ? 0.7 : 1
                }}
              >
                Close
              </button>
            </div>

            <div style={{
              flex: 1,
              overflow: 'auto',
              borderRadius: '10px',
              border: '1px solid var(--md-sys-color-outline-variant)',
              padding: '12px'
            }}>
              {inviteStep === 'selectChallenge' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                    Choose a challenge to copy members from.
                  </div>
                  {isLoadingInviteChallenges ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                      <md-circular-progress indeterminate></md-circular-progress>
                      Loading challenges...
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {inviteChallenges.map((challenge) => (
                        <button
                          key={challenge.id}
                          onClick={() => onSelectInviteChallenge(challenge.id)}
                          style={{
                            backgroundColor: 'var(--md-sys-color-surface)',
                            border: '1px solid var(--md-sys-color-outline)',
                            borderRadius: '10px',
                            padding: '10px 12px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            color: 'var(--md-sys-color-on-surface)'
                          }}
                        >
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {challenge.challenge_name || 'Untitled Challenge'}
                          </div>
                          <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                            {challenge.end_date || challenge.start_date || 'No dates'}
                          </div>
                        </button>
                      ))}
                      {inviteChallenges.length === 0 && (
                        <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                          No other challenges found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {inviteStep === 'selectUsers' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                    {selectedInviteChallenge?.challenge_name || 'Selected challenge'}
                  </div>
                  {isLoadingInviteMembers ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--md-sys-color-on-surface-variant)' }}>
                      <md-circular-progress indeterminate></md-circular-progress>
                      Loading members...
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => onSelectAllInviteMembers(true)}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'var(--md-sys-color-primary)',
                            border: '1px solid var(--md-sys-color-outline)',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          Select all
                        </button>
                        <button
                          onClick={() => onSelectAllInviteMembers(false)}
                          style={{
                            backgroundColor: 'transparent',
                            color: 'var(--md-sys-color-primary)',
                            border: '1px solid var(--md-sys-color-outline)',
                            borderRadius: '20px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          Clear all
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {inviteChallengeMembers.map((member) => {
                          const isSelected = selectedInviteMemberIds.has(member.id);
                          return (
                            <label
                              key={member.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 10px',
                                borderRadius: '10px',
                                border: '1px solid var(--md-sys-color-outline-variant)',
                                backgroundColor: 'var(--md-sys-color-surface)',
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => onToggleInviteMember(member.id)}
                              />
                              <div>
                                <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500', fontSize: '14px' }}>
                                  {member.displayName}
                                </div>
                                <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '12px' }}>
                                  {member.email || member.id}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                        {inviteChallengeMembers.length === 0 && (
                          <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                            No members found.
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {inviteStep === 'confirm' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                    {selectedInviteChallenge?.challenge_name || 'Selected challenge'}
                  </div>
                  <div style={{ color: 'var(--md-sys-color-on-surface)', fontWeight: '500' }}>
                    {selectedInviteMemberIds.size} users selected
                  </div>
                  <div style={{ color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
                    Email preferences will be preserved from the source challenge.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
              <button
                onClick={() => {
                  if (inviteStep === 'selectUsers') {
                    setInviteStep('selectChallenge');
                    return;
                  }
                  if (inviteStep === 'confirm') {
                    setInviteStep('selectUsers');
                  }
                }}
                {...(inviteStep === 'selectChallenge' ? { disabled: true } : {})}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--md-sys-color-primary)',
                  border: '1px solid var(--md-sys-color-outline)',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: inviteStep === 'selectChallenge' ? 'not-allowed' : 'pointer',
                  opacity: inviteStep === 'selectChallenge' ? 0.7 : 1
                }}
              >
                Back
              </button>
              {inviteStep === 'selectUsers' && (
                <button
                  onClick={() => setInviteStep('confirm')}
                  {...((isLoadingInviteMembers || selectedInviteMemberIds.size === 0) ? { disabled: true } : {})}
                  style={{
                    backgroundColor: 'var(--md-sys-color-primary)',
                    color: 'var(--md-sys-color-on-primary)',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: (isLoadingInviteMembers || selectedInviteMemberIds.size === 0) ? 'not-allowed' : 'pointer',
                    opacity: (isLoadingInviteMembers || selectedInviteMemberIds.size === 0) ? 0.7 : 1
                  }}
                >
                  Continue
                </button>
              )}
              {inviteStep === 'confirm' && (
                <button
                  onClick={onConfirmInviteMembers}
                  {...(isAddingInviteMembers ? { disabled: true } : {})}
                  style={{
                    backgroundColor: 'var(--md-sys-color-primary)',
                    color: 'var(--md-sys-color-on-primary)',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: isAddingInviteMembers ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isAddingInviteMembers ? 0.7 : 1
                  }}
                >
                  {isAddingInviteMembers ? (
                    <>
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid currentColor',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <Icon name="joinChallenge" />
                      Add users
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showRemoveMemberModal}
        onClose={onCloseRemoveMemberModal}
        onConfirm={onConfirmRemoveMember}
        title="Remove Member?"
        message={`Remove ${removeMemberTarget?.displayName || 'this member'} from the challenge? This can’t be undone, and they would have to rejoin themselves.`}
        confirmText="Remove"
        confirmIcon="removePerson"
        isDestructive={true}
        isLoading={isRemovingMember}
      />

      <ConfirmationModal
        isOpen={showAdminRoleModal}
        onClose={onCloseAdminRoleModal}
        onConfirm={onConfirmAdminRoleChange}
        title={adminRoleNextValue ? "Make Admin?" : "Remove Admin?"}
        message={
          adminRoleNextValue
            ? `Make ${adminRoleTarget?.displayName || 'this member'} an admin? They can rename the challenge, manage admins, and remove members.`
            : `Remove admin role for ${adminRoleTarget?.displayName || 'this member'}? They will no longer be able to manage the challenge.`
        }
        confirmText={adminRoleNextValue ? "Make Admin" : "Remove Admin"}
        confirmIcon={adminRoleNextValue ? "verifiedUser" : "removeModerator"}
        isDestructive={!adminRoleNextValue}
        isLoading={!!adminUpdateUserId}
      />

      {/* QR Code Modal */}
      {showQRModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--md-sys-color-surface)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              color: 'var(--md-sys-color-on-surface)', 
              marginBottom: '16px',
              marginTop: 0
            }}>
              Share Challenge
            </h3>
            <img 
              src={qrCodeDataURL} 
              alt="QR Code for challenge" 
              style={{ 
                maxWidth: '256px', 
                width: '100%', 
                height: 'auto',
                marginBottom: '16px'
              }} 
            />
            <p style={{ 
              color: 'var(--md-sys-color-on-surface-variant)', 
              fontSize: '14px',
              marginBottom: '16px'
            }}>
              Scan this QR code to join the challenge
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button 
                onClick={handleCopyLink}
                style={{
                  backgroundColor: 'var(--md-sys-color-primary)',
                  color: 'var(--md-sys-color-on-primary)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Copy Link
              </button>
              <button 
                onClick={() => setShowQRModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--md-sys-color-primary)',
                  border: '1px solid var(--md-sys-color-outline)',
                  borderRadius: '20px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmationModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={confirmArchiveChallenge}
        title={userChallengeData?.archived ? "Unarchive Challenge?" : "Archive Challenge?"}
        message={userChallengeData?.archived 
          ? "Are you sure you want to unarchive this challenge? It will appear in your active challenges again and email updates will be re-enabled." 
          : "Are you sure you want to archive this challenge? It will be moved to your archived challenges and email updates will be disabled."}
        confirmText={userChallengeData?.archived ? "Unarchive" : "Archive"}
        confirmIcon={userChallengeData?.archived ? "unarchiveChallenge" : "archiveChallenge"}
        isLoading={isArchiving}
      />

      {/* Leave Challenge Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onConfirm={confirmLeaveChallenge}
        title="Leave Challenge?"
        message="Are you sure you want to leave this challenge? This action cannot be undone and you will lose all your progress."
        confirmText="Leave Challenge"
        confirmIcon="leaveChallenge"
        isDestructive={true}
        isLoading={isLeavingJoining}
      />
    </div>
  );
};
